/* eslint-disable react/no-unescaped-entities */
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const AcceptableUsePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-xl">Acceptable Use Policy</h1>
              <p className="text-sm text-slate-400">Last updated: January 13, 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-3xl font-bold mb-6">SlideCon Acceptable Use Policy</h1>
          
          <p className="text-slate-300 mb-6">
            This Acceptable Use Policy ("AUP") governs your use of SlideCon's services and 
            is designed to ensure a safe, respectful, and productive environment for all users. 
            By using SlideCon, you agree to comply with this policy.
          </p>

          <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-6 my-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Zero Tolerance Policy: Sexual & Nudity Content
            </h2>
            <p className="text-slate-200 mb-4">
              <strong>SlideCon maintains an absolute zero-tolerance policy for sexual content 
              and nudity.</strong> This platform is designed for professional, educational, and 
              religious purposes. The following content is strictly prohibited:
            </p>
            <ul className="list-disc pl-6 text-slate-200 space-y-3">
              <li>
                <strong>Pornographic Material:</strong> Any sexually explicit images, videos, 
                text, or other content depicting sexual acts
              </li>
              <li>
                <strong>Nudity:</strong> Any images or videos depicting full or partial nudity, 
                including but not limited to exposed genitalia, buttocks, or female breasts
              </li>
              <li>
                <strong>Sexual Imagery:</strong> Sexually suggestive content, even if not 
                explicitly nude, including provocative poses or clothing
              </li>
              <li>
                <strong>Sexual Text Content:</strong> Erotic stories, sexual descriptions, 
                or sexually suggestive text
              </li>
              <li>
                <strong>AI-Generated Sexual Content:</strong> Using our AI tools to generate, 
                modify, or enhance any sexual or nude content
              </li>
              <li>
                <strong>Links to Adult Content:</strong> Sharing links or references to 
                external sexual or adult content
              </li>
            </ul>
            <div className="mt-6 p-4 bg-red-950/50 rounded-lg">
              <p className="text-red-300 font-semibold">
                CONSEQUENCES: Violation of this policy will result in immediate account 
                termination, permanent ban from the platform, and potential reporting to 
                law enforcement authorities where applicable.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold mt-8 mb-4">1. Permitted Uses</h2>
          <p className="text-slate-300">SlideCon is designed for:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Creating professional presentations and slideshows</li>
            <li>Religious and worship-related content (sermons, hymns, scripture)</li>
            <li>Educational materials and classroom presentations</li>
            <li>Business and corporate presentations</li>
            <li>Community organization and communication</li>
            <li>Personal creative projects (within policy guidelines)</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">2. Prohibited Content</h2>
          <p className="text-slate-300">In addition to sexual and nudity content, the following is prohibited:</p>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">Illegal Content</h3>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Content that violates any federal, state, or local law</li>
            <li>Child Sexual Abuse Material (CSAM) - immediately reported to NCMEC and law enforcement</li>
            <li>Content promoting illegal drug use or trafficking</li>
            <li>Content facilitating fraud or financial crimes</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Harmful Content</h3>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Violence, gore, or graphic content</li>
            <li>Hate speech, discrimination, or content targeting protected groups</li>
            <li>Harassment, bullying, or threatening content</li>
            <li>Self-harm or suicide-promoting content</li>
            <li>Terrorism or extremist content</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Deceptive Content</h3>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Misinformation or disinformation</li>
            <li>Impersonation of others</li>
            <li>Phishing or social engineering attempts</li>
            <li>Spam or unsolicited commercial content</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Intellectual Property Violations</h3>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Copyright-infringing content</li>
            <li>Trademark violations</li>
            <li>Unauthorized use of others' likeness or identity</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">3. AI Usage Guidelines</h2>
          <p className="text-slate-300">When using our AI features, you agree to:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Not attempt to bypass content filters or safety measures</li>
            <li>Not use AI to generate prohibited content categories</li>
            <li>Review AI-generated content before publishing or sharing</li>
            <li>Not use AI to create deceptive deepfakes or misleading content</li>
            <li>Clearly label AI-generated content when appropriate</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">4. Community Standards</h2>
          <p className="text-slate-300">When participating in community features, you agree to:</p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Treat all community members with respect</li>
            <li>Not share others' personal information without consent</li>
            <li>Keep communications professional and appropriate</li>
            <li>Report violations you observe to our moderation team</li>
            <li>Follow community-specific rules set by administrators</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">5. Reporting Violations</h2>
          <p className="text-slate-300">
            If you encounter content that violates this policy, please report it immediately:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Use the in-app "Report" feature on any content</li>
            <li>Email: cs@myrdkllc.com</li>
            <li>For urgent matters involving child safety, contact local law enforcement and email us at cs@myrdkllc.com</li>
          </ul>
          <p className="text-slate-300 mt-3">
            All reports are reviewed promptly and kept confidential.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">6. Enforcement</h2>
          <p className="text-slate-300">
            Violations of this policy may result in the following actions, at our sole discretion:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li><strong>Warning:</strong> First-time minor violations may receive a warning</li>
            <li><strong>Content Removal:</strong> Offending content will be immediately removed</li>
            <li><strong>Temporary Suspension:</strong> Account access may be suspended pending review</li>
            <li><strong>Permanent Ban:</strong> Serious or repeated violations result in permanent termination</li>
            <li><strong>Legal Action:</strong> Illegal content will be reported to appropriate authorities</li>
          </ul>

          <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 my-6">
            <p className="text-amber-300">
              <strong>Note:</strong> Sexual content, nudity, and child safety violations result 
              in immediate permanent ban without warning. No appeals will be considered for 
              these violations.
            </p>
          </div>

          <h2 className="text-2xl font-semibold mt-8 mb-4">7. Appeals Process</h2>
          <p className="text-slate-300">
            If you believe your account was suspended in error (except for sexual content/nudity 
            violations), you may appeal by:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Emailing cs@myrdkllc.com within 30 days of suspension</li>
            <li>Providing your account information and explanation</li>
            <li>Waiting for review (typically 5-10 business days)</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">8. Policy Updates</h2>
          <p className="text-slate-300">
            We may update this Acceptable Use Policy from time to time. Continued use of 
            SlideCon after changes constitutes acceptance of the updated policy. We will 
            notify users of significant changes.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">9. Contact</h2>
          <div className="bg-slate-800 rounded-lg p-4 mt-4">
            <p className="text-slate-300">
              <strong>SlideCon (a DBA of RDK Services LLC)</strong><br /><br />
              <strong>Customer Service:</strong><br />
              cs@myrdkllc.com<br /><br />
              <strong>Mailing Address:</strong><br />
              15810 Gale Ave. Suite 285<br />
              Hacienda Heights, CA 91745<br />
              United States
            </p>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              By using SlideCon, you acknowledge that you have read, understood, and agree 
              to comply with this Acceptable Use Policy. Violations may result in account 
              termination and legal action where appropriate.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AcceptableUsePage;
